import { startTransition, useState } from 'react';
import useSWR from 'swr/immutable';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useDeviceHeader } from '@packages/shared/src/providers/DeviceHeaderProvider/useDeviceHeader';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';

import type { NavigationData } from './types';
import { getNavigationDataEndpoint } from './getNavigationDataEndpoint';
import { fetchRequiredStaticJson } from '../api';

/**
 * Fetch navigation data from the API
 *
 * @returns navigation data, initially only the first few levels ("sliced"), and a function to load the full data on-demand
 */
export const useNavigationData = (): {
  data: NavigationData;
  loadFullData: () => void;
} => {
  const {
    clientId,
    navigation: { loadSliced, dataBucket },
  } = useConfig();
  const deploymentEnv = useSWR<string>('deploymentEnv');
  const [sliced, setSliced] = useState(loadSliced);
  const { locale } = useI18n();
  // Navigation JSONs are prebuilt per UA-derived device (web/mob).
  // Reuse that header channel so the server and client resolve the same file and skip redundant fetches.
  const device = useDeviceHeader();
  const channel = device === 'desktop' ? 'web' : 'mob';

  const endpoint = getNavigationDataEndpoint(
    dataBucket,
    clientId,
    channel,
    locale,
    sliced,
    deploymentEnv.data,
  );

  const { data } = useSWR<NavigationData>(
    endpoint,
    (url: string) => fetchRequiredStaticJson(url, { componentName: 'useNavigationData' }),
    { keepPreviousData: true },
  );

  const loadFullData = () => {
    startTransition(() => setSliced(false));
  };

  return { data: data || [], loadFullData };
};
